import { useState, useEffect } from 'react';
import { variablesTema, cargarFuente } from '../tema';
import { useParams } from 'react-router-dom';
import RoulettePopup from '../components/RoulettePopup';
import { money, fmtDate, accesoLabel } from '../events/util';
import { usePresale } from '../hooks/usePresale';
import { orgHeader } from '../tenant';
import VenueNotFound from '../components/VenueNotFound';

const API = process.env.REACT_APP_API_URL || '';

export default function Home({ onNotFound }) {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const url = slug ? `${API}/api/public/home/${slug}` : `${API}/api/public/home`;
    fetch(url, { headers: orgHeader() }).then(r => r.ok ? r.json() : Promise.reject(r)).then(d => { setData(d); cargarFuente(d && d.org && d.org.tipografia); })
      .catch(err => {
        // 404 = ese boliche no existe (subdominio desconocido/mal tipeado) → página propia.
        // Otros errores (red/500) → comportamiento anterior (no confundir un blip con "no existe").
        if (err && err.status === 404) setError('notfound');
        else { setError(true); onNotFound && onNotFound(); }
      });
  }, [slug]); // eslint-disable-line

  if (error === 'notfound') return <VenueNotFound />;
  if (error) return null;
  if (!data) return <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'grid', placeItems: 'center', color: 'var(--ink-600)' }}>Cargando…</div>;

  const { org, sections, events, albums, sessions, points } = data;
  const on = (k) => sections?.[k] !== false;
  const NAV = [['noches', 'Próximos eventos', '#noches'], on('galeria') && ['galeria', 'Galería', '#galeria'], on('sessions') && ['sessions', 'Sessions', '#sessions'], on('privados') && ['privados', 'Privados', '#privados']].filter(Boolean);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', ...variablesTema(org) }}>
      {on('ruleta') && data.ruleta_event_id && <RoulettePopup eventId={data.ruleta_event_id} />}

      {/* NAVBAR */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {org.logo_url
            ? <img src={org.logo_url} alt={org.name} style={{ height: 30, objectFit: 'contain' }} />
            : <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16, color: '#fff', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{org.name}</span>}
          <div className="home-nav" style={{ display: 'flex', gap: 22, alignItems: 'center' }}>
            {NAV.map(([k, label, href]) => <a key={k} href={href} style={navLink}>{label}</a>)}
            <a href={`/cuenta/${org.slug}`} style={{ ...navLink, color: '#fff' }}>{accesoLabel(org.slug)}</a>
          </div>
          <button className="home-burger" onClick={() => setMenu(m => !m)} style={{ display: 'none', background: 'transparent', border: 0, color: '#fff', fontSize: 22 }}>☰</button>
        </div>
        {menu && (
          <div className="home-sheet" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {NAV.map(([k, label, href]) => <a key={k} href={href} onClick={() => setMenu(false)} style={sheetLink}>{label}</a>)}
            <a href={`/cuenta/${org.slug}`} style={sheetLink}>{accesoLabel(org.slug)}</a>
          </div>
        )}
      </div>

      {/* HERO */}
      <div style={{ position: 'relative', minHeight: '78vh', display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: org.hero_url ? `center/cover no-repeat url(${org.hero_url})` : 'linear-gradient(135deg, var(--red-900), #000)', filter: 'grayscale(0.4) brightness(0.45)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #000 4%, rgba(0,0,0,0.2) 60%)' }} />
        <div style={{ position: 'relative', maxWidth: 1100, margin: '0 auto', width: '100%', padding: '0 20px 56px' }}>
          {/* detalle superior: línea roja + eyebrow */}
          {org.hero_eyebrow && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              <span style={{ width: 44, height: 2, background: 'var(--red-500)', display: 'block' }} />
              <span style={{ color: '#fff', fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase', fontWeight: 600 }}>{org.hero_eyebrow}</span>
            </div>
          )}
          {/* nombre dominante */}
          <div className="lb-display" style={{ fontSize: 'clamp(52px, 11vw, 130px)', lineHeight: 0.9 }}>{org.hero_name || org.name}</div>
          {/* tagline debajo, blanco, con palabra en itálica */}
          {org.tagline && (
            <div style={{ color: '#fff', fontSize: 'clamp(18px, 2.6vw, 28px)', marginTop: 18, fontFamily: 'var(--font-display)', fontWeight: 500, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
              {renderTagline(org.tagline)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 30, flexWrap: 'wrap' }}>
            <a href="#noches" className="lb-btn lb-btn--primary">Comprar QR →</a>
            {on('galeria') && <a href="#galeria" className="lb-btn lb-btn--ghost">Ver galería de fotos ↓</a>}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px' }}>
        {/* LAS NOCHES */}
        <Section id="noches" title="Próximos eventos">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 20 }}>
            {events.map(e => <EventCard key={e.id} e={e} />)}
            {events.length === 0 && <div style={{ color: 'var(--ink-600)' }}>No hay próximas noches publicadas.</div>}
          </div>
        </Section>

        {/* GALERÍA */}
        {on('galeria') && albums.length > 0 && (
          <div id="galeria" style={{ paddingTop: 64, scrollMarginTop: 70 }}>
            <div style={{ color: 'var(--red-400)', fontSize: 12, letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>Galería</div>
            <div className="lb-display" style={{ fontSize: 'clamp(32px, 6vw, 60px)', marginBottom: 24 }}>
              Las noches de <em style={{ fontFamily: 'var(--font-serif, Georgia)', fontStyle: 'italic', textTransform: 'none', fontWeight: 400 }}>{org.hero_name || org.name}</em>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
              {albums.map((a, i) => (
                <a key={i} href={a.drive_link} target="_blank" rel="noreferrer" className="lb-card" style={{ overflow: 'hidden', textDecoration: 'none' }}>
                  <div style={{ aspectRatio: '16/9', background: a.cover_url ? `center/cover no-repeat url(${a.cover_url})` : 'var(--ink-200)' }} />
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, color: 'var(--ink-600)', textTransform: 'capitalize' }}>{fmtDate(a.date)}</div>
                    <div style={{ color: '#fff', fontWeight: 600, marginTop: 2 }}>{a.title}</div>
                    <div style={{ color: 'var(--red-400)', fontSize: 12, marginTop: 8 }}>Ver álbum ↗</div>
                  </div>
                </a>
              ))}
            </div>
            {org.gallery_url && (
              <div style={{ marginTop: 24 }}>
                <a href={org.gallery_url} target="_blank" rel="noreferrer" className="lb-btn lb-btn--ghost">Ver todas las galerías →</a>
              </div>
            )}
          </div>
        )}

        {/* SESSIONS */}
        {on('sessions') && sessions.length > 0 && (
          <div id="sessions" style={{ paddingTop: 64, scrollMarginTop: 70 }}>
            <div style={{ color: 'var(--red-400)', fontSize: 12, letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>YouTube</div>
            <div className="lb-display" style={{ fontSize: 'clamp(32px, 6vw, 60px)', marginBottom: 24 }}>{org.sessions_title || 'Sessions'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
              {sessions.map((s, i) => (
                <a key={i} href={s.youtube_url} target="_blank" rel="noreferrer" className="lb-card" style={{ overflow: 'hidden', textDecoration: 'none' }}>
                  <div style={{ aspectRatio: '16/9', background: s.thumbnail_url ? `center/cover no-repeat url(${s.thumbnail_url})` : 'var(--ink-200)' }} />
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ color: 'var(--ink-600)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{s.label || `Session #${s.session_number}`}</div>
                    <div style={{ color: '#fff', fontWeight: 600, marginTop: 3 }}>{s.dj_name}</div>
                  </div>
                </a>
              ))}
            </div>
            {org.youtube_channel_url && (
              <div style={{ marginTop: 24 }}>
                <a href={org.youtube_channel_url} target="_blank" rel="noreferrer" className="lb-btn lb-btn--ghost">Ver todos →</a>
              </div>
            )}
          </div>
        )}

        {/* PRIVADOS */}
        {on('privados') && (
          <Section id="privados" title={org.privados_titulo || 'Privados'}>
            {/* Un solo bloque: texto + foto. Cada venue vende algo distinto (15, casamientos,
                corporativos), así que el título, el texto y la imagen los edita el dueño en
                Mi Web → Identidad. Sin imagen, el texto ocupa todo el ancho. */}
            <div className="lb-card tk-2col" style={{ padding: 0, overflow: 'hidden', display: 'grid', gridTemplateColumns: org.privados_img ? '1.1fr 1fr' : '1fr', gap: 0, alignItems: 'stretch' }}>
              <div style={{ padding: 'clamp(24px,4vw,34px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <p style={{ color: 'var(--ink-700)', fontSize: 15, lineHeight: 1.65, whiteSpace: 'pre-line', margin: 0, maxWidth: '54ch' }}>
                  {/* El nombre del hero suele venir con punto final ("LOBBO."): se saca para
                      no terminar en dos puntos seguidos. */}
                  {org.privados_texto || `Festejá tu cumpleaños, tu egresados o tu evento corporativo con la producción de ${String(org.hero_name || org.name || 'la casa').replace(/[.\s]+$/, '')}. Escribinos y armamos la noche a tu medida.`}
                </p>
                {org.whatsapp && (
                  <div style={{ marginTop: 22 }}>
                    <a href={`https://wa.me/${org.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="lb-btn lb-btn--primary lb-btn--sm">Consultar</a>
                  </div>
                )}
              </div>
              {org.privados_img && (
                <div style={{ minHeight: 260, background: `center/cover no-repeat #0E0E0E url(${org.privados_img})` }} />
              )}
            </div>
          </Section>
        )}

        {/* PUNTOS */}
        {on('puntos') && points.catalog.length > 0 && (
          <Section id="puntos" title="Canjeá tus puntos">
            <p style={{ color: 'var(--ink-700)', fontSize: 14, marginTop: -8, marginBottom: 16 }}>Sumás puntos en cada compra. Creá tu cuenta y empezá a juntar para canjear por estos premios.</p>
            {/* Misma grilla que la galería de álbumes: 3 columnas en PC, 1 en celular. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
              {points.catalog.map(c => {
                const talles = (Array.isArray(c.variants) ? c.variants : []).filter(v => v.stock == null || v.stock > 0);
                return (
                  <div key={c.id} className="lb-card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {/* La foto arriba, cuadrada. Va `cover`: la foto ya se recorta cuadrada al
                        subirla (`squareImage`), así que acá no recorta nada — y las que se
                        cargaron antes de eso quedan igual encuadradas en vez de estiradas.
                        Cuando el premio no tiene foto va igual el bloque del mismo alto: si no,
                        las tarjetas de una fila quedan desparejas y la grilla se ve rota. */}
                    <div style={{ position: 'relative', aspectRatio: '1 / 1', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                      {c.image_url
                        ? <img src={c.image_url} alt={c.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        : <span style={{ fontSize: 30, opacity: 0.35 }}>🎁</span>}
                    </div>
                    <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <div style={{ color: 'var(--red-400)', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22 }}>{c.points_required} pts</div>
                      <div style={{ color: '#fff', fontWeight: 600, marginTop: 4 }}>{c.name}</div>
                      {c.description && <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>{c.description}</div>}
                      {/* Solo los talles QUE QUEDAN: mostrar uno agotado es prometerlo. */}
                      {talles.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                          {talles.map(v => (
                            <span key={v.label} style={{ fontSize: 10.5, letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border)', color: 'var(--ink-800)' }}>{v.label}</span>
                          ))}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 8 }}>{c.unlimited || c.stock == null ? 'Sin límite' : `${c.stock} disponibles`}</div>
                      {/* El espaciador empuja el botón al fondo: así queda a la misma altura
                          en toda la fila aunque una tarjeta tenga descripción larga y otra no.
                          Va como div aparte para no pisarle el padding a la clase del botón. */}
                      <div style={{ flex: 1, minHeight: 12 }} />
                      <a href={`/cuenta/${org.slug}`} className="lb-btn lb-btn--ghost lb-btn--sm lb-btn--block">Mi cuenta</a>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}
      </div>

      {/* FOOTER */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 60, padding: '32px 20px', textAlign: 'center' }}>
        {org.logo_url && <img src={org.logo_url} alt="" style={{ height: 28, opacity: 0.8, objectFit: 'contain' }} />}
        <div style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 12 }}>{org.name}</div>
        {org.footer_text && <div style={{ color: 'var(--ink-600)', fontSize: 11, marginTop: 6, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{org.footer_text}</div>}
        <div style={{ color: '#3D3D3D', fontSize: 11, marginTop: 16 }}>Powered by tukipro</div>
      </div>

      <style>{`
        @media (max-width: 760px) {
          .home-nav { display: none !important; }
          .home-burger { display: block !important; }
          .home-priv { grid-template-columns: 1fr !important; }
        }
        .home-sheet { display: flex; flex-direction: column; }
        .home-sheet a { padding: 14px 22px; border-bottom: 1px solid rgba(255,255,255,0.06); color: var(--ink-700); text-decoration: none; font-size: 13px; letter-spacing: 0.14em; text-transform: uppercase; }
      `}</style>
    </div>
  );
}

const navLink = { color: 'var(--ink-700)', textDecoration: 'none', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600 };
const sheetLink = navLink;

// Tarjeta de evento del home. En presale muestra PRESALE + cuenta regresiva en
// lugar del botón de compra; al llegar la hora vuelve "Comprar entrada".
function EventCard({ e }) {
  const presale = usePresale(e.sales_start_at);
  return (
    <div className="lb-card" style={{ overflow: 'hidden' }}>
      <div style={{ aspectRatio: '1/1', background: e.flyer_url ? `center/cover no-repeat url(${e.flyer_url})` : 'linear-gradient(135deg, var(--red-900), var(--ink-200))', position: 'relative' }}>
        {presale.active && <span style={{ position: 'absolute', top: 10, left: 10, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 10, letterSpacing: '0.2em', color: '#fff', background: 'var(--red-600)', borderRadius: 999, padding: '4px 12px' }}>PRESALE</span>}
      </div>
      <div style={{ padding: '16px 18px' }}>
        <div style={{ color: 'var(--red-400)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>{fmtDate(e.date)}</div>
        <div className="lb-h" style={{ fontSize: 20, marginTop: 4 }}>{e.name}</div>
        {Array.isArray(e.lineup) && e.lineup.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 6 }}>{e.lineup.map(r => Array.isArray(r) ? r[0] : r.name).filter(Boolean).slice(0, 3).join(' · ')}</div>
        )}
        {presale.active ? (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--ink-600)', textTransform: 'uppercase', marginBottom: 6 }}>La venta abre en</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', fontFamily: 'var(--font-display)', fontWeight: 800, color: '#fff' }}>
              <span>{presale.d}<small style={{ fontSize: 10, color: 'var(--ink-600)', fontWeight: 600 }}>d</small></span>
              <span>{String(presale.h).padStart(2, '0')}<small style={{ fontSize: 10, color: 'var(--ink-600)', fontWeight: 600 }}>h</small></span>
              <span>{String(presale.m).padStart(2, '0')}<small style={{ fontSize: 10, color: 'var(--ink-600)', fontWeight: 600 }}>m</small></span>
              <span>{String(presale.s).padStart(2, '0')}<small style={{ fontSize: 10, color: 'var(--ink-600)', fontWeight: 600 }}>s</small></span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
            {e.price_from != null && <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>Desde <b style={{ color: '#fff' }}>{money(e.price_from)}</b></span>}
            <a href={`/e/${e.id}`} className="lb-btn lb-btn--primary lb-btn--sm">Comprar entrada →</a>
          </div>
        )}
        {/* La lista de cumpleaños va acá abajo y no dentro del bloque de arriba: durante la
            presale ese bloque es la cuenta regresiva, y el cumpleañero se tiene que poder
            anotar igual aunque la venta todavía no haya abierto. */}
        {e.cumple_abierto && (
          <a href={`/e/${e.id}/cumple`} className="lb-btn lb-btn--ghost lb-btn--sm lb-btn--block" style={{ marginTop: 8 }}>🎂 Lista de cumpleaños</a>
        )}
      </div>
    </div>
  );
}

// Renderiza el tagline: la palabra entre *asteriscos* va en itálica (serif)
function renderTagline(text) {
  return String(text).split(/(\*[^*]+\*)/g).filter(Boolean).map((part, i) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i} style={{ fontFamily: 'var(--font-serif, Georgia)', fontStyle: 'italic', textTransform: 'lowercase', fontWeight: 400 }}>{part.slice(1, -1)}</em>;
    }
    return <span key={i}>{part}</span>;
  });
}

function Section({ id, title, action, children }) {
  return (
    <div id={id} style={{ paddingTop: 64, scrollMarginTop: 70 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div className="lb-display" style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
